import React, { useMemo } from 'react';
import { BarChart3, Users, Clock as ClockIcon, ListChecks } from 'lucide-react';
import { PageLayout, PageHeader, PageCard, pageStyles } from '../PageLayout';
import { useTheme } from '../../contexts/ThemeContext';

type AnyUser = {
    id: string;
    name: string;
    role?: string;
    adv?: string;
    btnr?: string;
    allowedToView?: string[];
};

type LogEntry = {
    id?: string;
    action?: string;
    message?: string;
    createdAt?: string;
    user?: string;
    [k: string]: any;
};

type StatsViewProps = {
    users: AnyUser[];
    logs: LogEntry[];
    customers: any[];
    tasks: any[];
    contractStates: Record<string, any>;
    currentUser: AnyUser | null;
};

const normalizeNumber = (v: any) =>
    v ? String(v).replace(/[^0-9]/g, '').replace(/^0+/, '') : '';

const normalizeName = (n: any) =>
    n ? n.replace(/[:.;,]/g, ' ').replace(/\s+/g, ' ').trim() : '';

const checkMatch = (allowed: AnyUser[], c: any) => {
    if (!allowed || !Array.isArray(allowed)) return false;
    if (allowed.some(u => u.role === 'admin')) return true;
    return allowed.some(u => {
        if (!u) return false;
        const uAdv = normalizeNumber(u.adv);
        const cAdp = normalizeNumber(c.adp);
        const cBtnr = normalizeNumber(c.btnr);
        const uBtnr = normalizeNumber(u.btnr);
        if (uAdv.length > 2 && ((cAdp && uAdv.includes(cAdp)) || (cBtnr && uAdv.includes(cBtnr)))) return true;
        if (uBtnr.length > 2 && ((cAdp && uBtnr.includes(cAdp)) || (cBtnr && uBtnr.includes(cBtnr)))) return true;
        const uName = normalizeName(u.name).toLowerCase();
        const cAdpName = normalizeName(c.adp).toLowerCase();
        return uName.length > 2 && cAdpName.includes(uName);
    });
};

const parseAnyDate = (s: any) => {
    if (!s) return null;
    if (typeof s === 'string' && s.includes('-')) {
        const [y, m, d] = s.split('-');
        return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    }
    if (typeof s === 'string' && s.includes('.')) {
        const [d, m, y] = s.split('.');
        return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    }
    return null;
};

export const StatsView: React.FC<StatsViewProps> = ({
    users,
    logs,
    customers,
    tasks,
    contractStates,
    currentUser,
}) => {
    const { isDark } = useTheme();

    const thirtyDaysAgo = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d;
    }, []);

    const employeeStats = useMemo(() => {
        const nonHiddenUsers = (users || []).filter(
            // keine Admins, keine ausgeblendeten – Innendienst jetzt EINBLENDEN
            (u: any) =>
                (!u.role || u.role !== 'admin') &&
                !u.hidden
        );

        return nonHiddenUsers.map(user => {
            // Login-Statistik aus Protokoll (logs)
            const userLoginLogs = (logs || []).filter(
                l =>
                    l.action === 'Login' &&
                    (l.user === user.name || (l.message && String(l.message).includes(user.name)))
            );
            const lastLogin = userLoginLogs.reduce<Date | null>((acc, l) => {
                if (!l.createdAt) return acc;
                const d = new Date(l.createdAt);
                if (!acc || d > acc) return d;
                return acc;
            }, null);
            const loginCount30 = userLoginLogs.filter(l => {
                if (!l.createdAt) return false;
                const d = new Date(l.createdAt);
                return d >= thirtyDaysAgo;
            }).length;

            // Kunden / Verträge nach Sichtbarkeit (ähnlich Dashboard/ExpirationView)
            const allowedUserIds = [user.id, ...(user.allowedToView || [])];
            const visibleUsers = users.filter(u => allowedUserIds.includes(u.id));
            const myCustomers = customers.filter(c => checkMatch(visibleUsers as AnyUser[], c));

            let contractsTotal = 0;
            let expirationsOpen = 0;
            let expirationsDone = 0;

            myCustomers.forEach(c => {
                const vertr = c.verträge || [];
                contractsTotal += vertr.length;
                vertr.forEach((v: any) => {
                    const safeId = (v.vsnr || '').replace(/\//g, '_');
                    const expiryDate = parseAnyDate(v.ablauf);
                    if (!expiryDate) return;
                    // gleiche Standard-Logik wie Abläufe-Ansicht: nächster 5-Monats-Zeitraum
                    const defaultFrom = new Date(2000, 0, 1);
                    const defaultTo = new Date();
                    defaultTo.setMonth(defaultTo.getMonth() + 5);
                    if (expiryDate < defaultFrom || expiryDate > defaultTo) return;

                    const sparteLower = (v.sparte || '').toLowerCase().trim();
                    const isKfzOrLeben =
                        /kfz|kraftfahrzeug|lebensversicherung|^leben$/.test(sparteLower) ||
                        sparteLower === 'lv';
                    if (isKfzOrLeben) return;

                    const isDone = contractStates?.[safeId] === 'done';
                    if (isDone) expirationsDone++;
                    else expirationsOpen++;
                });
            });

            // Aufgaben-Statistik
            const myTasks = (tasks || []).filter(
                (t: any) => t.assignedTo === user.name
            );
            const openTasks = myTasks.filter((t: any) => t.status !== 'done').length;
            const doneTasks = myTasks.filter((t: any) => t.status === 'done').length;

            return {
                user,
                lastLogin,
                loginCount30,
                customersCount: myCustomers.length,
                contractsTotal,
                crossSellingQuote: myCustomers.length ? (contractsTotal / myCustomers.length) : 0,
                expirationsOpen,
                expirationsDone,
                openTasks,
                doneTasks,
            };
        });
    }, [users, logs, customers, tasks, contractStates, thirtyDaysAgo]);

    // Gesamt-Loginzahlen
    const totalLoginsAllTime = useMemo(
        () => (logs || []).filter((l: any) => l.action === 'Login').length,
        [logs]
    );
    const totalLogins30 = useMemo(
        () => employeeStats.reduce((sum, s) => sum + (s.loginCount30 || 0), 0),
        [employeeStats]
    );

    const isAdmin = currentUser?.role === 'admin';
    const mayViewStats = isAdmin || (currentUser as any)?.canViewStats;

    if (!mayViewStats) {
        return (
            <PageLayout>
                <PageHeader title="Statistik" />
                <PageCard>
                    <div className={pageStyles.empty(isDark)}>
                        Du hast keine Berechtigung, die Statistik zu sehen.
                    </div>
                </PageCard>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <PageHeader
                title="Statistik"
                subtitle="Überblick über Login-Verhalten, Abläufe und Aufgaben je Mitarbeiter"
                actions={
                    <div className="flex items-center gap-2 text-xs">
                        <BarChart3 className={isDark ? 'text-cyan-300' : 'text-cyan-600'} size={18} />
                        <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>
                            Letzte 30 Tage
                        </span>
                    </div>
                }
            />
            <PageCard>
                {employeeStats.length === 0 ? (
                    <div className={pageStyles.empty(isDark)}>
                        Keine Mitarbeiter gefunden.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-4 text-xs sm:text-sm">
                            <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>
                                Gesamtlogins (30 Tage):{' '}
                                <span className="font-semibold">{totalLogins30}</span>
                            </span>
                            <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>
                                Gesamtlogins (alle Zeiten):{' '}
                                <span className="font-semibold">{totalLoginsAllTime}</span>
                            </span>
                        </div>
                        <table className="min-w-full text-sm border-collapse">
                            <thead>
                                <tr className={isDark ? 'bg-slate-800/80 text-slate-200' : 'bg-slate-50 text-slate-700'}>
                                    <th className="px-3 py-2 text-left font-semibold">
                                        <div className="flex items-center gap-1">
                                            <Users size={14} />
                                            Mitarbeiter
                                        </div>
                                    </th>
                                    <th className="px-3 py-2 text-left font-semibold">
                                        <div className="flex items-center gap-1">
                                            <ClockIcon size={14} />
                                            Letzter Login
                                        </div>
                                    </th>
                                    <th className="px-3 py-2 text-center font-semibold">
                                        Logins (30 Tage)
                                    </th>
                                    <th className="px-3 py-2 text-center font-semibold">
                                        Kunden
                                    </th>
                                    <th className="px-3 py-2 text-center font-semibold">
                                        Verträge
                                    </th>
                                    <th className="px-3 py-2 text-center font-semibold">
                                        Abläufe offen / erledigt
                                    </th>
                                    <th className="px-3 py-2 text-center font-semibold">
                                        Aufgaben offen / erledigt
                                    </th>
                                    <th className="px-3 py-2 text-center font-semibold">
                                        Cross-Selling-Quote
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {employeeStats.map(stat => (
                                    <tr
                                        key={stat.user.id}
                                        className={isDark ? 'border-t border-slate-700' : 'border-t border-slate-100'}
                                    >
                                        <td className="px-3 py-2 align-top">
                                            <div className="font-semibold">
                                                {stat.user.name}
                                            </div>
                                            {stat.user.adv && (
                                                <div className="text-xs text-slate-500">
                                                    ADV/ADP: {stat.user.adv}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 align-top text-sm">
                                            {stat.lastLogin
                                                ? stat.lastLogin.toLocaleString('de-DE', {
                                                      day: '2-digit',
                                                      month: '2-digit',
                                                      year: 'numeric',
                                                      hour: '2-digit',
                                                      minute: '2-digit',
                                                  })
                                                : 'Noch nie'}
                                        </td>
                                        <td className="px-3 py-2 align-top text-center">
                                            {stat.loginCount30}
                                        </td>
                                        <td className="px-3 py-2 align-top text-center">
                                            {stat.customersCount}
                                        </td>
                                        <td className="px-3 py-2 align-top text-center">
                                            {stat.contractsTotal}
                                        </td>
                                        <td className="px-3 py-2 align-top text-center">
                                            <span className="inline-flex items-center justify-center gap-1 text-xs">
                                                <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">
                                                    {stat.expirationsOpen}
                                                </span>
                                                <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                                                    {stat.expirationsDone}
                                                </span>
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 align-top text-center">
                                            <span className="inline-flex items-center justify-center gap-1 text-xs">
                                                <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                                    {stat.openTasks}
                                                </span>
                                                <span className="px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700">
                                                    {stat.doneTasks}
                                                </span>
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 align-top text-center">
                                            {stat.customersCount
                                                ? (stat.crossSellingQuote as number)?.toFixed(2)
                                                : '0.00'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </PageCard>
        </PageLayout>
    );
};

export default StatsView;

